import { useCallback, useContext, useState } from 'react'
import { debounce } from 'lodash'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import { LegatMatterIntakeKindsDocument } from '@generatedTypes/graphql'
import { LegalMatterListLoading } from '@components/molecules/LoadingSkeletons'
import ErrorScreen from '@components/organisms/ErrorScreen'
import { UserProfileContext } from '@view/providers/UserProfileProvider'
import { useSubscribedProducts } from '@utils/useSubscribedProducts'
import ConfirmationDialog from '@components/molecules/ConfirmationDialog'
import { encodeToBase64 } from '@utils/StringFormatter'
import { routes } from '@/routes'
import StateCountySelect from '@components/molecules/StateCountySelect'
import { useUSStateDatabase } from '@utils/useUSStateDatabase'

interface SubcategoryType {
  key: string
  subcategoryName: string
  intakeForm: string
}

interface CategoryIntakeType {
  category: string
  subcategories: SubcategoryType[]
}

interface DialogState {
  open: boolean
  state?: string
  county?: string
  error?: string
  subCategoryKey?: string
}

const emptyDialog: DialogState = { open: false }

const LegalServiceSelection = (): JSX.Element => {
  const navigate = useNavigate()
  const { user } = useContext(UserProfileContext)
  const { states: usStates } = useUSStateDatabase({
    operationalStatesOnly: true,
  })
  const { subscribedProductsLoading, subscribedProducts } =
    useSubscribedProducts(user.key)
  const [dialog, setDialog] = useState<DialogState>(emptyDialog)

  const { data, loading, error } = useQuery(LegatMatterIntakeKindsDocument, {
    variables: {
      productIds: subscribedProducts.map((product) => product.id),
    },
    skip: subscribedProducts.length === 0,
  })

  const handleConfirm = (subCategory: SubcategoryType) => {
    setDialog({
      open: true,
      state: dialog.state ?? user.profile?.location?.state?.code,
      county: undefined,
      error: undefined,
      subCategoryKey: subCategory.key,
    })
  }

  const handleSelectChange = useCallback(
    debounce((state?: string, county?: string) => {
      setDialog((prev) => ({
        ...prev,
        state,
        county,
        error: undefined,
      }))
    }, 300),
    [],
  )

  if (user === null) return <ErrorScreen content="No user found" />

  if (loading || subscribedProductsLoading) {
    return <LegalMatterListLoading numberOfSkeletons={1} />
  }

  if (error !== undefined) {
    return (
      <ErrorScreen content="Sorry, we can not show results, try again later" />
    )
  }

  let gropedByCategory: CategoryIntakeType[] = []
  if (data?.legalMatterKinds && data?.legalMatterKinds.length > 0) {
    gropedByCategory = Object.values(
      data?.legalMatterKinds?.reduce((acc, item) => {
        if (!acc[item.category]) {
          // Create a new category if it doesn't exist
          acc[item.category] = {
            category: item.category,
            subcategories: [],
          }
        }

        // Add the subcategory to the relevant category
        acc[item.category].subcategories.push({
          subcategoryName: item.name,
          key: item.key,
          intakeForm: item.intakeForm,
        })

        return acc
      }, {}),
    )

    gropedByCategory.sort((a, b) => a.category.localeCompare(b.category))
  }

  const confirm = () => {
    const { state, county, subCategoryKey } = dialog

    if (!state || !county) {
      setDialog((prev) => ({
        ...prev,
        error: 'You have to select both state and county.',
      }))
      return
    }

    setDialog((prev) => ({ ...prev, error: undefined }))

    navigate(
      routes.createLegalMatter.param({
        legalMatterKindKey: subCategoryKey,
        stateCode: encodeToBase64(state),
        countyName: encodeToBase64(county),
      }),
    )
  }

  const cancel = () => {
    setDialog(emptyDialog)
  }

  return (
    <>
      <Typography
        variant="h1"
        gutterBottom
        data-testid="CreateLegalMatter-pageTitle"
      >
        Please Select Your Requested Legal Service
      </Typography>
      {gropedByCategory.length > 0 &&
        gropedByCategory.map((categoryItem) => {
          if (categoryItem.category === 'N/A') {
            return
          }

          return (
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={categoryItem.category}
                data-testid="kind-category"
              >
                <Typography variant="h3" color="primary">
                  {' '}
                  {categoryItem.category}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container>
                  {categoryItem.subcategories
                    .sort((a, b) =>
                      a.subcategoryName.localeCompare(b.subcategoryName),
                    )
                    .map((subcategory) => {
                      return (
                        <Grid
                          item
                          sx={{ padding: '0px 8px', marginBottom: '16px' }}
                        >
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                              handleConfirm(subcategory)
                            }}
                            data-testid="subcategory-button"
                          >
                            {subcategory.subcategoryName}
                          </Button>
                        </Grid>
                      )
                    })}
                </Grid>
              </AccordionDetails>
            </Accordion>
          )
        })}
      <ConfirmationDialog
        open={dialog.open}
        onClose={cancel}
        title={
          <Typography variant="h1" gutterBottom>
            Please choose legal matter state:
          </Typography>
        }
        content={
          loading ? (
            <Box display="flex" alignItems="center" mt={2} mb={2}>
              <CircularProgress size={20} sx={{ mr: 2 }} />
              <Typography>Please wait…</Typography>
            </Box>
          ) : (
            <StateCountySelect
              states={usStates}
              selectedState={dialog?.state}
              onChange={handleSelectChange}
            />
          )
        }
        actions={
          <>
            <Button
              onClick={confirm}
              variant="contained"
              data-testid="confirm-select-state-button"
            >
              Submit
            </Button>
            <Button
              onClick={() => setDialog(emptyDialog)}
              variant="contained"
              color="error"
              data-testid="cancel-select-state-button"
            >
              Cancel
            </Button>
          </>
        }
        errorMessage={dialog.error}
      />
    </>
  )
}

export default LegalServiceSelection
